import { gql as apolloGql } from '@apollo/client'
import { ADVISOR_ENGAGEMENT_CHAT_THREAD_FRAGMENT } from '@src/gql/fragments/AdvisorEngagementChatThreadFragment'
import { PAGINATION_FRAGMENT } from '@src/gql/fragments/PaginationFragment'
import { gql } from '@src/lib/gql'

/** Core founder engagement fields — no chatThread (avoids N+1 on list/chat resolve pages). */
const ADVISOR_FOUNDER_ENGAGEMENT_FRAGMENT = apolloGql`
  fragment AdvisorFounderEngagementFragment on ScoAdvisorEngagement {
    id
    advisorEngagementId
    founderUserId
    advisorUserId
    statusKey
    categoryKey
    whyText
    skillTagTitles
    industryKeys
    sourceKey
    isAI
    currentUserCanEdit
    notifyMatches
    groupRequestId
    invitedAt
    activatedAt
    endedAt
    chatLastActiveAt
    createdAt
    category {
      id
      key
      title
    }
    status {
      id
      key
      title
    }
    source {
      id
      key
      title
    }
    company {
      id
      name
      elevatorPitch
      stageKey
      industryKeys
      logoUrl
    }
    compensationTypeKeys
    timeCommitmentTypeKeys
    groupRequest {
      id
      groupRequestId
      publishStatusKey
      title
      needExplanation
    }
    currentAgreement {
      id
      advisorAgreementId
      statusKey
      equityPercent
      status {
        id
        key
        title
      }
    }
    founderUser {
      id
      userId
      slug
      fullName
      displayName
      pictureUrl
      title
      shortBio
      location
      company {
        id
        name
        elevatorPitch
        stageKey
        industryKeys
        logoUrl
      }
    }
    advisorUser {
      id
      userId
      slug
      fullName
      displayName
      pictureUrl
    }
  }
`

const ADVISOR_FOUNDER_ENGAGEMENT_WITH_CHAT_FRAGMENT = apolloGql`
  fragment AdvisorFounderEngagementWithChatFragment on ScoAdvisorEngagement {
    ...AdvisorFounderEngagementFragment
    ...AdvisorEngagementChatThreadFragment
  }
  ${ADVISOR_FOUNDER_ENGAGEMENT_FRAGMENT}
  ${ADVISOR_ENGAGEMENT_CHAT_THREAD_FRAGMENT}
`

/**
 * My Founders list + selection resolve.
 * Sorting/filtering/pagination are server-side (orderBy / search / page / pageSize).
 * No chatThread — those pages do not need unread badges.
 */
export const ADVISOR_FOUNDER_ENGAGEMENTS = gql`
  query AdvisorFounderEngagements(
    $statusKeys: [String]
    $page: Int
    $pageSize: Int
    $search: String
    $founderUserIds: [String]
    $orderBy: String
  ) {
    advisorEngagements(
      role: "ADVISOR"
      statusKeys: $statusKeys
      page: $page
      pageSize: $pageSize
      search: $search
      founderUserIds: $founderUserIds
      orderBy: $orderBy
    ) {
      id
      pagination {
        ...PaginationFragment
      }
      items {
        ...AdvisorFounderEngagementFragment
      }
    }
  }
  ${PAGINATION_FRAGMENT}
  ${ADVISOR_FOUNDER_ENGAGEMENT_FRAGMENT}
`

/**
 * Sidebar nav only — includes chatThread for unread badges.
 * Keep pageSize small; chat resolve uses denormalized chatThreadId (PK lookup).
 */
export const ADVISOR_FOUNDER_ENGAGEMENTS_WITH_CHAT = gql`
  query AdvisorFounderEngagementsWithChat(
    $statusKeys: [String]
    $page: Int
    $pageSize: Int
    $search: String
    $founderUserIds: [String]
    $orderBy: String
  ) {
    advisorEngagements(
      role: "ADVISOR"
      statusKeys: $statusKeys
      page: $page
      pageSize: $pageSize
      search: $search
      founderUserIds: $founderUserIds
      orderBy: $orderBy
    ) {
      id
      pagination {
        ...PaginationFragment
      }
      items {
        ...AdvisorFounderEngagementWithChatFragment
      }
    }
  }
  ${PAGINATION_FRAGMENT}
  ${ADVISOR_FOUNDER_ENGAGEMENT_WITH_CHAT_FRAGMENT}
`
